import type { SubscriptionInfo, UserSubscriptionStatus } from '@eci-saas/shared'
import type { Session, User } from '@supabase/supabase-js'
import { useSupabaseAuth } from '~/composables/utils/useSupabase'

export const useUserStore = defineStore(
  'user',
  () => {
    const user = ref<User | null>(null)
    const session = ref<Session | null>(null)
    const subscription = ref<SubscriptionInfo | null>(null)
    const subscriptionStatus = ref<UserSubscriptionStatus>({
      isActive: false,
      currentPlan: 'free',
      expiresAt: null,
      canUpgrade: true,
    })
    const initialized = ref(false)
    const loading = ref(false)
    const authUpdateInProgress = ref(false) // 防止并发更新
    const supabaseAuth = useSupabaseAuth()

    // 重置状态
    const reset = () => {
      user.value = null
      session.value = null
      subscription.value = null
      subscriptionStatus.value = {
        isActive: false,
        currentPlan: 'free',
        expiresAt: null,
        canUpgrade: true,
      }
    }

    // 设置用户信息（从useAuth调用）
    const setUser = (newUser: User | null) => {
      user.value = newUser
    }

    // 设置会话信息（从useAuth调用）
    const setSession = (newSession: Session | null) => {
      session.value = newSession
    }

    // 设置订阅信息（从useAuth调用）
    const setSubscription = (newSubscription: SubscriptionInfo | null) => {
      subscription.value = newSubscription
    }

    // 设置订阅状态（从useAuth调用）
    const setSubscriptionStatus = (newStatus: UserSubscriptionStatus) => {
      subscriptionStatus.value = newStatus
    }

    // 初始化用户状态
    const initializeAuth = async () => {
      if (initialized.value)
        return

      loading.value = true

      try {
        // 获取当前会话 - 这里还是需要调用 supabase 获取初始状态
        const { data: { session: currentSession }, error } = await supabaseAuth.getSession()

        if (error) {
          console.error('Error getting session:', error)
          reset()
        }
        else {
          session.value = currentSession
          user.value = currentSession?.user ?? null
        }

        // 标记初始化完成
        initialized.value = true

        // 监听认证状态变化 - 集中管理所有认证状态
        supabaseAuth.onAuthStateChange(async (event, newSession) => {
          // 防止并发更新
          if (authUpdateInProgress.value) {
            console.warn('Auth update already in progress, skipping:', event)
            return
          }

          authUpdateInProgress.value = true

          try {
            session.value = newSession
            user.value = newSession?.user ?? null

            // 处理不同的认证事件
            switch (event) {
              case 'SIGNED_OUT':
                reset()
                break
              case 'SIGNED_IN':
                // 用户登录时，更新订阅信息
                if (newSession?.user?.id) {
                  // 动态导入避免循环依赖
                  setTimeout(async () => {
                    const { useInit } = await import('~/composables/scenes/useInit')
                    const { onLoginSuccess } = useInit()
                    await onLoginSuccess(newSession.user.id)
                  }, 0)
                }
                break
              case 'TOKEN_REFRESHED':
                // 会话已更新
                break
              case 'PASSWORD_RECOVERY':
                // 处理密码恢复
                break
              default:
                break
            }
          }
          catch (error) {
            console.error('Error handling auth state change:', error)
          }
          finally {
            authUpdateInProgress.value = false
          }
        })
      }
      catch (error) {
        console.error('Failed to initialize auth:', error)
        reset()
      }
      finally {
        loading.value = false
        initialized.value = true // 即使出错也要标记为已初始化
      }
    }

    // 检查用户是否已登录
    const isAuthenticated = computed(() => {
      return !!user.value && !!session.value && !loading.value
    })

    // 获取用户显示名称
    const displayName = computed(() => {
      if (!user.value)
        return ''

      // 优先使用用户元数据中的名称
      const metadata = user.value.user_metadata
      if (metadata?.full_name)
        return metadata.full_name
      if (metadata?.first_name && metadata?.last_name) {
        return `${metadata.first_name} ${metadata.last_name}`
      }
      if (metadata?.first_name)
        return metadata.first_name

      // 备用：使用邮箱
      return user.value.email || 'User'
    })

    // 获取用户头像
    const avatarUrl = computed(() => {
      if (!user.value)
        return ''
      return user.value.user_metadata?.avatar_url || ''
    })

    return {
      // 只读状态
      user: readonly(user),
      session: readonly(session),
      subscription: readonly(subscription),
      subscriptionStatus: readonly(subscriptionStatus),
      initialized: readonly(initialized),
      loading: readonly(loading),

      // 计算属性
      isAuthenticated,
      displayName,
      avatarUrl,

      // 状态管理方法
      setUser,
      setSession,
      setSubscription,
      setSubscriptionStatus,
      initializeAuth,
      reset,
    }
  },
  {
    persist: true,
  },
)
